import React, { useEffect, useMemo, useState } from "react";
import { OGDB2 } from "../ogdb2Api";
import { OGDB2_METRICS, OGDB2_METRIC_CATEGORIES } from "../metricRegistry";
import BulkAnalysisColumnsModal from "../components/BulkAnalysisColumnsModal";
import MultiTokenInput from "../components/MultiTokenInput";
import ExportLeaseSummaryModal from "../components/ExportLeaseSummaryModal";
import BulkAnalysisAutoAskModal from "../components/BulkAnalysisAutoAskModal";
import ProductionDetailPopupWindow from "../components/ProductionDetailPopupWindow";
import DeclineAsksModal from "../components/DeclineAsksModal";
import MassLOEBasisModal from "../components/MassLOEBasisModal";

/**
 * Bulk Analysis (BAID / Units)
 * Vision:
 * - Columns are ONLY: identity + flags + registry metrics (+ optional deltas)
 * - No separate “metrics to analyze” section
 * - Column selector controls which columns + scenarios + deltas are visible
 * - Always request all registry metric_codes from backend so table can sort/filter on any registry metric
 */

const VIEW_KEY = "ogdb2_bulk_views_v3";
const LAST_VIEW_KEY = "ogdb2_bulk_last_view_v3";
const COL_UI_KEY = "ogdb2_bulk_columns_v1";
const AUTORUN_ONCE_KEY = "ogdb2_bulk_autorun_once_v1";
const AUTOSELECT_VIEW_ID_KEY = "ogdb2_bulk_autoselect_view_id_v1";

// BA Auto Ask undo (client-side safety net)
const AUTOASK_UNDO_KEY = "ogdb2_bulk_autoask_undo_v1";
const APPRAISAL_FIRM_OPTIONS = [
  { value: "", label: "Any" },
  { value: "Capitol Appraisal Group", label: "Capitol Appraisal Group" },
  { value: "Pritchard & Abbott", label: "Pritchard & Abbott" },
  { value: "Wardlaw Appraisal Group", label: "Wardlaw Appraisal Group" },
  { value: "Thomas Y. Pickett", label: "Thomas Y. Pickett" },
  { value: "__CUSTOM__", label: "Other (type it)..." },
];

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readViewsLocal() {
  const raw = localStorage.getItem(VIEW_KEY);
  const arr = safeJsonParse(raw || "[]", []);
  return Array.isArray(arr) ? arr : [];
}

function writeViewsLocal(views) {
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify(Array.isArray(views) ? views : []));
  } catch {}
}

function readLastView() {
  const raw = localStorage.getItem(LAST_VIEW_KEY);
  return raw ? safeJsonParse(raw, null) : null;
}

function writeLastView(viewState) {
  try {
    localStorage.setItem(LAST_VIEW_KEY, JSON.stringify(viewState || null));
  } catch {}
}

function readColumnUI() {
  const raw = localStorage.getItem(COL_UI_KEY);
  const v = raw ? safeJsonParse(raw, null) : null;
  return v && typeof v === "object" ? v : null;
}

function writeColumnUI(v) {
  try {
    localStorage.setItem(COL_UI_KEY, JSON.stringify(v || null));
  } catch {}
}

function fmt(x) {
  if (x === null || x === undefined || x === "") return "—";
  if (typeof x === "number") return x.toLocaleString();
  return String(x);
}

function fmtPct(p) {
  if (p === null || p === undefined) return "—";
  if (typeof p === "number") return `${p.toFixed(1)}%`;
  return String(p);
}

// Choose exactly ONE group for each metric to avoid duplicates.
// Precedence: PRODUCTION > EXPENSES > PRICE_DECK > VALUATION. (Ignore FACTS_QUICK)
function preferredMetricGroupKey(metricDef) {
  const cats = metricDef?.categories || [];
  if (cats.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION)) return "production";
  if (cats.includes(OGDB2_METRIC_CATEGORIES.EXPENSES)) return "expenses";
  if (cats.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK)) return "price_deck";
  if (cats.includes(OGDB2_METRIC_CATEGORIES.VALUATION)) return "valuation";
  return "other";
}

function compareValues(a, b, dir = "asc") {
  const mul = dir === "desc" ? -1 : 1;

  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1 * mul;
  if (bNull) return -1 * mul;

  if (typeof a === "number" && typeof b === "number") return (a - b) * mul;
  return String(a).localeCompare(String(b)) * mul;
}

function applyFilter(val, filter) {
  if (!filter) return true;
  const { op, value } = filter;

  if (op === "is_true") return Boolean(val) === true;
  if (op === "is_false") return Boolean(val) === false;

  const vNum = typeof val === "number" ? val : Number(String(val).replace(/,/g, ""));
  const fNum = Number(String(value).replace(/,/g, ""));

  if (op === "gt") return Number.isFinite(vNum) && Number.isFinite(fNum) && vNum > fNum;
  if (op === "gte") return Number.isFinite(vNum) && Number.isFinite(fNum) && vNum >= fNum;
  if (op === "lt") return Number.isFinite(vNum) && Number.isFinite(fNum) && vNum < fNum;
  if (op === "lte") return Number.isFinite(vNum) && Number.isFinite(fNum) && vNum <= fNum;
  if (op === "eq_num") return Number.isFinite(vNum) && Number.isFinite(fNum) && vNum === fNum;

  const s = String(val ?? "");
  const needle = String(value ?? "");
  if (op === "contains") return s.toLowerCase().includes(needle.toLowerCase());

  return true;
}

/**
 * Column key conventions:
 * - identity:
 *    unit.id
 *    unit.well_label   (computed: well_name fallback)
 *
 * - metrics:
 *    m.<code>.assessor
 *    m.<code>.actual
 *    m.<code>.third_party
 *    m.<code>.ba
 *
 * - deltas (computed client-side):
 *    d.<code>.act_abs  => actual - assessor
 *    d.<code>.act_pct  => (actual - assessor)/|assessor| * 100
 *    d.<code>.ba_abs   => ba - assessor
 *    d.<code>.ba_pct   => (ba - assessor)/|assessor| * 100
 *
 * - flags:
 *    f.<flag_key>
 */
function getCell(row, key) {
  const u = row?.unit || {};
  const res = row?.resolved || {};
  const flags = row?.flags || {};
  
  function toNum(x) {
    if (x === null || x === undefined || x === "") return null;
    if (typeof x === "number") return Number.isFinite(x) ? x : null;
    const n = Number(String(x).replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : null;
  }

  // ✅ Comparable LOE (derived):
  // - Assessor: imported LOE
  // - Actual: follows assessor basis (uses actual buckets)
  // - BA: defaults to assessor comparable (overrideable), basis always mirrors assessor
  function getComparableLOE(scenarioKey) {
    const assessorComparable = toNum(res?.loe?.assessor);

    if (scenarioKey === "assessor") return assessorComparable;

    // ✅ BA override wins (if present). Otherwise BA = assessor comparable.
    if (scenarioKey === "ba") {
      const override = toNum(res?.loe_comparable?.ba);
      return override !== null ? override : assessorComparable;
    }

    // Actual (bucket-based) always follows assessor basis
    const basisAssessor = String(res?.loe_basis?.assessor || "").trim(); // "gross" | "net_of_marketing" | ""
    const opex = toNum(res?.opex?.actual) || 0;
    const marketing = toNum(res?.marketing?.actual) || 0;

    const net = basisAssessor === "net_of_marketing";
    return net ? opex : opex + marketing;
  }
  
  // ✅ 3P Comparable Start Rate (derived)
  // Candidates come from third_party scenario, basis comes from:
  //   1) BA override (scenario ba)
  //   2) third_party default (scenario third_party)
  //   3) fallback = "sep_dec"
  function getComparable3PStartRate(product, scenarioKey) {
    // we only render this meaningfully on third_party (and optionally ba), but return it for any scenario if asked
    const basisCode = product === "oil" ? "tp_start_rate_basis_oil" : "tp_start_rate_basis_gas";
    const basis =
      String(res?.[basisCode]?.ba || "").trim() ||
      String(res?.[basisCode]?.third_party || "").trim() ||
      "sep_dec";

    const pick = (code) => {
      const v = res?.[code]?.third_party;
      const n = toNum(v);
      return n === null ? null : n;
    };

    const map = product === "oil"
      ? {
          sep_dec: "tp_oil_sr_sep_dec",
          last_1: "tp_oil_sr_last_1",
          last_3: "tp_oil_sr_last_3",
          last_4: "tp_oil_sr_last_4",
          last_6: "tp_oil_sr_last_6",
          last_12: "tp_oil_sr_last_12",
        }
      : {
          sep_dec: "tp_gas_sr_sep_dec",
          last_1: "tp_gas_sr_last_1",
          last_3: "tp_gas_sr_last_3",
          last_4: "tp_gas_sr_last_4",
          last_6: "tp_gas_sr_last_6",
          last_12: "tp_gas_sr_last_12",
        };

    const primaryCode = map[basis] || map.sep_dec;

    // If chosen basis candidate is missing, fallback order: sep_dec -> last_4 -> last_3 -> last_1
    return (
      pick(primaryCode) ??
      pick(map.sep_dec) ??
      pick(map.last_4) ??
      pick(map.last_3) ??
      pick(map.last_1)
    );
  }

  if (key === "unit.well_label") {
    // Prefer well_name, then display_name, then lease_name, then rrc.
    return u?.well_name || u?.display_name || u?.lease_name || u?.rrc_number || u?.id;
  }

  if (key.startsWith("unit.")) {
    const k = key.slice("unit.".length);

    // Never format identifiers with commas
    if (k === "id") {
      const v = u?.[k];
      return v === null || v === undefined ? v : String(v).replace(/,/g, "");
    }

    return u?.[k];
  }
  
  // Monthly production pivot columns (injected client-side into each row):
  // keys look like: pm.oil_01, pm.gas_12, etc.
  if (key.startsWith("pm.")) {
    const field = key.slice("pm.".length); // e.g., "oil_01"
    const mp = row?.monthly_pivot || {};
    return mp?.[field];
  }

  if (key.startsWith("m.")) {
    const parts = key.split(".");
    const metric = parts[1];
    const scenario = parts[2]; // assessor|actual|third_party|ba
    const m = res?.[metric] || {};
    const v = m?.[scenario];
	
	if (metric === "loe_comparable") {
      return getComparableLOE(scenario);
    }
	
	if (metric === "comparable_oil_start_rate") {
      if (scenario !== "third_party") return v;
      return getComparable3PStartRate("oil", scenario);
    }

    if (metric === "comparable_gas_start_rate") {
      if (scenario !== "third_party") return v;
      return getComparable3PStartRate("gas", scenario);
    }

    // ✅ 3P basis columns should display BA override (if set), even when viewing the (3P) column.
    // This makes the UI reflect what you selected, while still preserving the true 3P default.
    if (metric === "tp_start_rate_basis_oil" || metric === "tp_start_rate_basis_gas") {
      if (scenario === "third_party") {
        const baVal = res?.[metric]?.ba;
        if (baVal !== null && baVal !== undefined && String(baVal).trim() !== "") return baVal;
      }
      return v;
    }

    // Identifiers should not show thousands separators
    if (metric === "lease_number") {
      return v === null || v === undefined ? v : String(v).replace(/,/g, "");
    }

    return v;
  }
  

  if (key.startsWith("d.")) {
    const parts = key.split(".");
    const metric = parts[1];
    const kind = parts[2]; // act_abs|act_pct|ba_abs|ba_pct
    // Special case: derived Comparable LOE
    if (metric === "loe_comparable") {
      const a = getComparableLOE("assessor");
      const act = getComparableLOE("actual");
      const ba = getComparableLOE("ba");

      if (typeof a !== "number") return null;

      if (kind === "act_abs") {
        if (typeof act !== "number") return null;
        return act - a;
      }
      if (kind === "act_pct") {
        if (typeof act !== "number") return null;
        if (a === 0) return null;
        return ((act - a) / Math.abs(a)) * 100.0;
      }
      if (kind === "ba_abs") {
        if (typeof ba !== "number") return null;
        return ba - a;
      }
      if (kind === "ba_pct") {
        if (typeof ba !== "number") return null;
        if (a === 0) return null;
        return ((ba - a) / Math.abs(a)) * 100.0;
      }
      return null;
    }

    const m = res?.[metric] || {};
    const a = m?.assessor;
    const act = m?.actual;
    const ba = m?.ba;

    if (typeof a !== "number") return null;

    if (kind === "act_abs") {
      if (typeof act !== "number") return null;
      return act - a;
    }
    if (kind === "act_pct") {
      if (typeof act !== "number") return null;
      if (a === 0) return null;
      return ((act - a) / Math.abs(a)) * 100.0;
    }
    if (kind === "ba_abs") {
      if (typeof ba !== "number") return null;
      return ba - a;
    }
    if (kind === "ba_pct") {
      if (typeof ba !== "number") return null;
      if (a === 0) return null;
      return ((ba - a) / Math.abs(a)) * 100.0;
    }
    return null;
  }

  if (key.startsWith("f.")) {
    const k = key.slice("f.".length);
    return flags?.[k];
  }

  return null;
}

function csvEscape(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function Section({ title, children, right }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-sm font-semibold text-gray-800">{title}</div>
        {right || null}
      </div>
      {children}
    </div>
  );
}

// Objective-ish metrics that should never get deltas unless explicitly overridden in the modal
const DEFAULT_OBJECTIVE_CODES = new Set([
  "lease_name",
  "lease_number",
  "rrc_number",
  "rrc",
  "well_name",
  "display_name",
  "client",
  "county",
  "operator",
  "interest_type",
  "interest_percent",
  "external_account_id",
  "account_id",
  "appraisal_firm",
]);

function buildMetricColumnKeysFromLegacy({ selectedMetricCodes, scenarios, showDeltas, metricIndex }) {
  const codes = Array.isArray(selectedMetricCodes) ? selectedMetricCodes : [];
  const sc = scenarios || { assessor: true, actual: true, third_party: false, ba: true };
  const deltasOn = showDeltas !== false;

  const keys = [];

  for (const code of codes) {
    if (!code) continue;

    // scenarios
    if (sc.assessor) keys.push(`m.${code}.assessor`);
    if (sc.actual) keys.push(`m.${code}.actual`);
    if (sc.third_party) keys.push(`m.${code}.third_party`);
    if (sc.ba) keys.push(`m.${code}.ba`);

    // deltas (only if negotiable)
    if (!deltasOn) continue;
    if (!sc.assessor) continue; // deltas are always vs assessor
    if (DEFAULT_OBJECTIVE_CODES.has(code)) continue;

    const def = metricIndex?.get(code);
    const cats = def?.categories || [];
    const negotiable =
      cats.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION) ||
      cats.includes(OGDB2_METRIC_CATEGORIES.EXPENSES) ||
      cats.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK) ||
      cats.includes(OGDB2_METRIC_CATEGORIES.VALUATION);

    if (!negotiable) continue;

    if (sc.actual) {
      keys.push(`d.${code}.act_abs`);
      keys.push(`d.${code}.act_pct`);
    }
    if (sc.ba) {
      keys.push(`d.${code}.ba_abs`);
      keys.push(`d.${code}.ba_pct`);
    }
  }

  // de-dupe preserving order
  const seen = new Set();
  return keys.filter((k) => {
    if (!k) return false;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export default function BulkAnalysisPage({ onBack, onOpenUnit, onOpenWell }) {
  const openUnit = onOpenUnit || onOpenWell;

  // ---------- Controls ----------
  const yearNow = new Date().getFullYear();
  const [client, setClient] = useState("");
  const [taxYear, setTaxYear] = useState(String(yearNow));
  const [limit, setLimit] = useState(500);

  const [q, setQ] = useState("");
  const [county, setCounty] = useState("");
  const [operator, setOperator] = useState("");
  const [appraisalFirm, setAppraisalFirm] = useState("");
  const [rrc, setRrc] = useState("");
  const [leaseNumbers, setLeaseNumbers] = useState([]); // pasted tokens (Lease Number (A) list)
  const [leaseName, setLeaseName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [interestType, setInterestType] = useState("");
  
  // ---------- Production detail popup (separate window) ----------
  const [prodPopupOpen, setProdPopupOpen] = useState(false);
  const [prodPopupUnitId, setProdPopupUnitId] = useState(null);
  const [prodPopupResolved, setProdPopupResolved] = useState(null);

  // Basis controls are stored as BA facts:
  // - tp_start_rate_basis_oil
  // - tp_start_rate_basis_gas
  async function setBasisForUnit(unitId, metric, value) {
    if (!unitId) return;
    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

    try {
      await OGDB2.upsertBAIDFact(unitId, {
        tax_year: ty,
        scenario: "ba",
        metric_code: metric,
        value_num: null,
        value_text: String(value || "").trim() || null,
        unit: null,
      });

      // Optimistic update: patch rows + the popup resolvedFacts in-place
      setRows((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex((r) => r?.unit?.id === unitId);
        if (idx === -1) return prev;

        const r0 = next[idx];
        const resolved0 = r0?.resolved || {};
        const metricRow = resolved0?.[metric] || {};
        const resolved1 = { ...resolved0, [metric]: { ...metricRow, ba: String(value || "").trim() } };

        next[idx] = { ...r0, resolved: resolved1 };

        // Keep popup in sync if it's showing the same unit
        if (Number(prodPopupUnitId) === Number(unitId)) {
          setProdPopupResolved(resolved1);
        }

        return next;
      });
    } catch (e) {
      window.alert(e?.response?.data?.detail || e?.message || "Failed to set basis");
    }
  }

  // Optional: hide metrics that are totally empty in results
  const [hideEmptyMetricCols, setHideEmptyMetricCols] = useState(true);
  const [showThirdParty, setShowThirdParty] = useState(true);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // ---------- Monthly production columns (pivot) ----------
  const [showMonthlyProd, setShowMonthlyProd] = useState(false);
  const [monthlyProdMode, setMonthlyProdMode] = useState("both"); // "both" | "oil" | "gas"
  const [prodYear, setProdYear] = useState(String(Number(taxYear) - 1));
  const monthlyPivotReqRef = React.useRef({ key: null, ids: null });
  
  const leaseSuggestions = useMemo(() => {
    // suggestions from current result set (fast, no extra API calls)
    const seen = new Set();
    const out = [];
    for (const r of rows || []) {
      const u = r?.unit || {};
      const v = String(u?.rrc_number || "").trim();
      if (!v) continue;
      if (seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
    return out.slice(0, 2000);
  }, [rows]);
  
  // -------- Column UX (remove from selection + drag reorder) --------
  const [dragColKey, setDragColKey] = useState(null);
  
  // --- Pin columns (sticky left) ---
  const headerCellRefs = React.useRef({});
  const [colWidths, setColWidths] = useState({});
  const autorunPendingRef = React.useRef(false);
  const hydratedRef = React.useRef(false);
  const pendingAutoViewIdRef = React.useRef(null);
  const autoViewAppliedRef = React.useRef(false); // ✅ prevents autorun before view state is applied
  const suppressPersistRef = React.useRef(false);

  // Measure header cell widths (works because header reflects column widths)
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const next = {};
      for (const [key, el] of Object.entries(headerCellRefs.current || {})) {
        if (el && el.getBoundingClientRect) {
          next[key] = el.getBoundingClientRect().width;
        }
      }
      setColWidths(next);
    });

    for (const el of Object.values(headerCellRefs.current || {})) {
      if (el) ro.observe(el);
    }

    return () => ro.disconnect();
  }, []);

  function pinnedLeftOffsetPx(colKey) {
    // IMPORTANT:
    // Pinned column ordering must follow the table's current visible order,
    // not the order the user pinned columns.
    const orderedPinnedKeys = (Array.isArray(visibleColumns) ? visibleColumns : [])
      .map((c) => c?.key)
      .filter(Boolean)
      .filter((k) => isPinned(k));

    let left = 0;
    for (const k of orderedPinnedKeys) {
      if (k === colKey) break;
      left += colWidths[k] || 0;
    }
    return left;
  }

  function pinnedStyle(colKey, isHeader = false) {
    // Header row should always be sticky to the top while scrolling.
    // Pinned columns also stick to the left.
    const baseHeader = isHeader
      ? { position: "sticky", top: 0, zIndex: 20, background: "#f9fafb" }
      : null;

    // Not pinned: only apply the sticky header behavior (top:0) if header.
    if (!isPinned(colKey)) return baseHeader;

    const left = pinnedLeftOffsetPx(colKey);

    // Pinned: combine sticky top (header) + sticky left (pinned)
    return {
      position: "sticky",
      top: isHeader ? 0 : undefined,
      left: `${left}px`,
      zIndex: isHeader ? 30 : 10,
      background: isHeader ? "#f9fafb" : "white",
      boxShadow: "2px 0 0 rgba(0,0,0,0.06)",
    };
  }

  function setColumnOrder(nextOrder) {
    setColUI((prev) => ({ ...(prev || {}), columnOrder: Array.isArray(nextOrder) ? nextOrder : [] }));
  }
  
  function getPinnedKeys() {
    return Array.isArray(colUI?.pinnedKeys) ? colUI.pinnedKeys : [];
  }

  function isPinned(colKey) {
    return getPinnedKeys().includes(colKey);
  }

  function togglePinned(colKey) {
    if (!colKey) return;
    setColUI((prev) => {
      const next = { ...(prev || {}) };
      const pins = Array.isArray(next.pinnedKeys) ? [...next.pinnedKeys] : [];
      const idx = pins.indexOf(colKey);
      if (idx >= 0) pins.splice(idx, 1);
      else pins.push(colKey);
      next.pinnedKeys = pins;
      return next;
    });
  }

  function clearPinned() {
    setColUI((prev) => ({ ...(prev || {}), pinnedKeys: [] }));
  }
  
  function normalizeColumnOrder(order, visibleKeys) {
    const vis = Array.isArray(visibleKeys) ? visibleKeys.filter(Boolean) : [];
    const setVis = new Set(vis);

    // keep only keys that still exist
    const base = Array.isArray(order) ? order.filter((k) => setVis.has(k)) : [];

    // append any missing visible keys at the end
    const setBase = new Set(base);
    for (const k of vis) {
      if (!setBase.has(k)) base.push(k);
    }

    return base;
  }

  function moveKeyInOrder(order, fromKey, toKey) {
    const arr = Array.isArray(order) ? [...order] : [];
    const fromIdx = arr.indexOf(fromKey);
    const toIdx = arr.indexOf(toKey);
    if (fromIdx === -1 || toIdx === -1) return arr;
    arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, fromKey);
    return arr;
  }

  function removeColumnFromSelection(colKey) {
    if (!colKey) return;

    setColUI((prev) => {
      const next = { ...(prev || {}) };

      // identity
      if (colKey.startsWith("unit.")) {
        const keys = Array.isArray(next.identityKeys) ? next.identityKeys : [];
        next.identityKeys = keys.filter((k) => k !== colKey);
      }

      // metrics/deltas: remove ONLY the specific column key
      if (colKey.startsWith("m.") || colKey.startsWith("d.")) {
        const keys = Array.isArray(next.metricColumnKeys) ? next.metricColumnKeys : [];
        next.metricColumnKeys = keys.filter((k) => k !== colKey);
      }

      // prune columnOrder so it doesn’t keep dead keys
      const order = Array.isArray(next.columnOrder) ? next.columnOrder : [];
      if (order.length) {
        next.columnOrder = order.filter((k) => k !== colKey);
      }

      return next;
    });
  }
  
  // -------- Inline Editing (Bulk Analysis) --------
  // Edit behavior:
  // - Double-click a BA metric cell to edit
  // - Enter / blur saves
  // - Esc cancels
  const [editing, setEditing] = useState(null); // { unitId, metric, scenario:'ba', key }
  const [editValue, setEditValue] = useState("");
  const [savingCell, setSavingCell] = useState(null); // { unitId, key }

  function toNumLoose(x) {
    if (x === null || x === undefined || x === "") return null;
    if (typeof x === "number") return Number.isFinite(x) ? x : null;
    const n = Number(String(x).replace(/[$,]/g, "").trim());
    return Number.isFinite(n) ? n : null;
  }

  function metricDefFor(code) {
    return metricIndex.get(code) || null;
  }

  function isEditableMetric(code) {
    if (!code) return false;

    // allow BA override directly
    if (code === "loe_comparable") return true;
	
	if (code === "loe_basis") return false;

    const def = metricDefFor(code);
    if (!def) return false;

    // only driver metrics should be edited (except loe_comparable override)
    if (def.kind && String(def.kind).toLowerCase() !== "driver") return false;

    const editable = Array.isArray(def.editableScenarios) ? def.editableScenarios : [];
    return editable.includes("ba");
  }

  function isEditableCell(colKey) {
    // Default rule: only allow editing for m.<code>.ba
    // Exception: allow editing the 3P basis columns (third_party) so user can set BA basis quickly.
    if (!colKey || typeof colKey !== "string") return false;
    if (!colKey.startsWith("m.")) return false;

    const parts = colKey.split(".");
    const code = parts[1];
    const scenario = parts[2];

    // ✅ Normal BA editing
    if (scenario === "ba") {
      return isEditableMetric(code);
    }

    // ✅ Special-case: allow clicking the (3P) basis cells to edit (writes to BA)
    if (
      scenario === "third_party" &&
      (code === "tp_start_rate_basis_oil" || code === "tp_start_rate_basis_gas")
    ) {
      return true;
    }

    return false;
  }

  function getRawCellValueForEdit(row, colKey) {
    // For edit input, return a clean raw string (no commas)

    // ✅ If user clicked the (3P) basis column, prefer showing the BA override if it exists.
    if (colKey === "m.tp_start_rate_basis_oil.third_party") {
      const ba = getCell(row, "m.tp_start_rate_basis_oil.ba");
      const v = (ba !== null && ba !== undefined && String(ba).trim() !== "") ? ba : getCell(row, colKey);
      if (v === null || v === undefined) return "";
      return String(v).replace(/,/g, "");
    }

    if (colKey === "m.tp_start_rate_basis_gas.third_party") {
      const ba = getCell(row, "m.tp_start_rate_basis_gas.ba");
      const v = (ba !== null && ba !== undefined && String(ba).trim() !== "") ? ba : getCell(row, colKey);
      if (v === null || v === undefined) return "";
      return String(v).replace(/,/g, "");
    }

    const v = getCell(row, colKey);
    if (v === null || v === undefined) return "";
    return String(v).replace(/,/g, "");
  }

  function startEdit(row, colKey) {
    const u = row?.unit || {};
    const unitId = u?.id;
    if (!unitId) return;

    const parts = colKey.split(".");
    const metric = parts[1];
    const scenario = parts[2];

    // don't allow editing while saving another cell
    if (savingCell) return;

    setEditing({ unitId, metric, scenario, key: colKey });
    setEditValue(getRawCellValueForEdit(row, colKey));
  }

  function cancelEdit() {
    setEditing(null);
    setEditValue("");
  }

  async function commitEdit(row, overrideValue) {
    if (!editing) return;

    const u = row?.unit || {};
    const unitId = u?.id;
    if (!unitId) return;

    const metric = editing.metric;
    const def = metricDefFor(metric) || { code: metric, type: "number", unit: null };

    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

    // Decide value_num vs value_text
    let value_num = null;
    let value_text = null;

    const raw = String((overrideValue !== undefined ? overrideValue : editValue) ?? "").trim();


    if (!raw) {
      value_num = null;
      value_text = null;
    } else if (String(def.type).toLowerCase() === "text") {
      value_text = raw;
    } else {
      const n = toNumLoose(raw);
      if (n === null) {
        // fallback: store as text if not parseable
        value_text = raw;
      } else {
        value_num = n;
      }
    }

    setSavingCell({ unitId, key: editing.key });

    try {
      await OGDB2.upsertBAIDFact(unitId, {
        tax_year: ty,
        scenario: "ba",
        metric_code: metric,
        value_num,
        value_text,
        unit: def.unit || null,
        // updated_by omitted (BulkAnalysisPage does not receive email)
      });

      // Optimistic local update so the table stays in sync immediately
      setRows((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex((r) => r?.unit?.id === unitId);
        if (idx === -1) return prev;

        const r0 = next[idx];
        const resolved0 = r0?.resolved || {};
        const metricRow = resolved0?.[metric] || {};

        // store either number or text (Bulk table can render both)
        const newVal = value_num !== null ? value_num : (value_text !== null ? value_text : null);

        let resolved1 = {
          ...resolved0,
          [metric]: { ...metricRow, ba: newVal },
        };

        next[idx] = { ...r0, resolved: resolved1 };
        return next;
      });

      cancelEdit();
    } catch (e) {
      window.alert(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSavingCell(null);
    }
  }
  const [hasRun, setHasRun] = useState(false);

  // Sorting + per-column filters
  const [sort, setSort] = useState({ key: "unit.well_label", dir: "asc" });
  const [colFilters, setColFilters] = useState({});

  // Views (stable selection by id to avoid name collisions)
  const [views, setViews] = useState(() => readViewsLocal());
  const [selectedViewId, setSelectedViewId] = useState("");

  function findViewById(id) {
    if (!id) return null;
    return views.find((v) => String(v?.id || "") === String(id)) || null;
  }

  // Columns modal
  const [columnsOpen, setColumnsOpen] = useState(false);
  
  // ---------- Export (Lease Summary XLSX) ----------
  const [exportOpen, setExportOpen] = useState(false);
  // ---------- BA Auto Ask ----------
  const [autoAskOpen, setAutoAskOpen] = useState(false);
  // ---------- Decline Asks ----------
  const [declineAsksOpen, setDeclineAsksOpen] = useState(false);
  // ---------- Mass LOE Basis ----------
  const [massBasisOpen, setMassBasisOpen] = useState(false);

  async function applyMassLOEBasis({ unitIds, newBasis, onProgress }) {
    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;
    if (!ty) throw new Error("Invalid tax year");
    const list = (Array.isArray(unitIds) ? unitIds : []).map(Number).filter(Number.isFinite);
    if (!list.length) return;

    const CONCURRENCY = 5;
    let idx = 0;
    let done = 0;

    const worker = async () => {
      while (true) {
        const i = idx++;
        if (i >= list.length) return;
        const unitId = list[i];

        // Only the assessor's loe_basis is written. The resolver mirrors
        // actual & BA basis to whatever the assessor has, so a single write
        // propagates everywhere on next read.
        await OGDB2.upsertBAIDFact(unitId, {
          tax_year: ty,
          scenario: "assessor",
          metric_code: "loe_basis",
          value_num: null,
          value_text: newBasis,
          unit: null,
        });

        // Optimistic local update so the table reflects the change immediately
        setRows((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          const rowIdx = next.findIndex((r) => r?.unit?.id === unitId);
          if (rowIdx === -1) return prev;
          const r0 = next[rowIdx];
          const resolved0 = r0?.resolved || {};
          const basisRow = resolved0?.loe_basis || {};
          const resolved1 = {
            ...resolved0,
            loe_basis: { ...basisRow, assessor: newBasis, actual: newBasis, ba: newBasis },
          };
          next[rowIdx] = { ...r0, resolved: resolved1 };
          return next;
        });

        done += 1;
        try { onProgress?.({ done, total: list.length }); } catch {}
      }
    };

    const workers = Array.from({ length: CONCURRENCY }, () => worker());
    await Promise.all(workers);
  }

  async function applyDeclineAsks({ actions }) {
    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;
    if (!ty) throw new Error("Invalid tax year");

    // Concurrency limiter (mirrors BA Auto Ask style)
    const list = Array.isArray(actions) ? actions : [];
    const CONCURRENCY = 5;

    let idx = 0;
    const worker = async () => {
      while (true) {
        const i = idx++;
        if (i >= list.length) return;

        const job = list[i]; // { unitId, setTo }
        const unitId = Number(job?.unitId);
        if (!Number.isFinite(unitId)) continue;

        const metric = "decline_rate";
        const def = metricDefFor(metric) || { code: metric, type: "percent", unit: "%" };

        const setTo = job?.setTo;
        const num = typeof setTo === "number" ? setTo : null;

        await OGDB2.upsertBAIDFact(unitId, {
          tax_year: ty,
          scenario: "ba",
          metric_code: metric,
          value_num: num,
          value_text: null,
          unit: def.unit || null,
        });

        // Optimistic local update (keep table in sync)
        setRows((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          const rowIdx = next.findIndex((r) => r?.unit?.id === unitId);
          if (rowIdx === -1) return prev;

          const r0 = next[rowIdx];
          const resolved0 = r0?.resolved || {};
          const metricRow = resolved0?.[metric] || {};
          const resolved1 = { ...resolved0, [metric]: { ...metricRow, ba: num } };

          next[rowIdx] = { ...r0, resolved: resolved1 };
          return next;
        });
      }
    };

    const workers = Array.from({ length: CONCURRENCY }, () => worker());
    await Promise.all(workers);
  }
  
  const [autoAskBusy, setAutoAskBusy] = useState(false);
  const [autoAskProgress, setAutoAskProgress] = useState(null); // { done, total }
  const [autoAskLastPreview, setAutoAskLastPreview] = useState(null);
  const [autoAskUndo, setAutoAskUndo] = useState(() => {
  try {
    const raw = localStorage.getItem(AUTOASK_UNDO_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
  
  
});

function saveAutoAskUndo(v) {
  try {
    if (!v) localStorage.removeItem(AUTOASK_UNDO_KEY);
    else localStorage.setItem(AUTOASK_UNDO_KEY, JSON.stringify(v));
  } catch {}
  setAutoAskUndo(v || null);
}

async function runUndoLastAutoAsk() {
  if (!autoAskUndo?.items || !Array.isArray(autoAskUndo.items) || autoAskUndo.items.length === 0) {
    window.alert("Nothing to undo yet.");
    return;
  }

  const ok = window.confirm(
    `Undo will revert ${autoAskUndo.items.length} BA values from your last BA Auto Ask run.\n\nContinue?`
  );
  if (!ok) return;

  const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

  setAutoAskBusy(true);
  setAutoAskProgress({ done: 0, total: autoAskUndo.items.length });

  const CONCURRENCY = 5;
  let idx = 0;
  let done = 0;

  const worker = async () => {
    while (true) {
      const i = idx++;
      if (i >= autoAskUndo.items.length) return;

      const job = autoAskUndo.items[i]; // { unitId, metric, prevBA }
      const def = metricDefFor(job.metric) || { code: job.metric, unit: null };

      try {
        const prev = job.prevBA;

        await OGDB2.upsertBAIDFact(job.unitId, {
          tax_year: ty,
          scenario: "ba",
          metric_code: job.metric,
          value_num: typeof prev === "number" ? prev : null,
          value_text: null,
          unit: def.unit || null,
        });

        // Optimistic local update
        setRows((prevRows) => {
          const next = Array.isArray(prevRows) ? [...prevRows] : [];
          const rowIdx = next.findIndex((r) => r?.unit?.id === job.unitId);
          if (rowIdx === -1) return prevRows;

          const r0 = next[rowIdx];
          const resolved0 = r0?.resolved || {};
          const metricRow = resolved0?.[job.metric] || {};
          const resolved1 = { ...resolved0, [job.metric]: { ...metricRow, ba: typeof prev === "number" ? prev : null } };
          next[rowIdx] = { ...r0, resolved: resolved1 };
          return next;
        });
      } catch (e) {
        console.error("Undo BA Auto Ask failed:", job, e);
      } finally {
        done += 1;
        setAutoAskProgress({ done, total: autoAskUndo.items.length });
      }
    }
  };

  try {
    const workers = Array.from({ length: CONCURRENCY }, () => worker());
    await Promise.all(workers);

    window.alert("Undo complete.");
    saveAutoAskUndo(null);
  } finally {
    setAutoAskBusy(false);
    setAutoAskProgress(null);
  }
}

  // Mirrors backend ASK_DEFS beneficial_direction rules
  // (higher = better for taxpayer; lower = better for taxpayer)
  const BA_AUTO_ASK_LEVERS = useMemo(
    () => [
      { code: "loe_comparable", label: "BA Ask - LOE (Comparable)", direction: "higher" },
      { code: "ad_valorem", label: "BA Ask - Ad Valorem", direction: "higher" },
      { code: "comparable_oil_start_rate", label: "BA Ask - Comparable Oil Start Rate", direction: "lower" },
      { code: "comparable_gas_start_rate", label: "BA Ask - Comparable Gas Start Rate", direction: "lower" },
      { code: "oil_price", label: "BA Ask - Oil Price", direction: "lower" },
      { code: "gas_price", label: "BA Ask - Gas Price", direction: "lower" },
      { code: "decline_rate", label: "BA Ask - Decline Rate", direction: "higher" },
      { code: "discount_rate", label: "BA Ask - Discount Rate", direction: "higher" },
      { code: "net_equipment_value", label: "BA Ask - Net Equipment Value", direction: "lower" },
    ],
    []
  );

  function leverDirection(code) {
    const hit = (BA_AUTO_ASK_LEVERS || []).find((x) => x.code === code);
    return hit?.direction || "lower";
  }

  function scenarioValue(row, metricCode, scenarioKey) {
    // IMPORTANT: uses the same getCell() that already handles derived Comparable LOE and 3P start rates
    return getCell(row, `m.${metricCode}.${scenarioKey}`);
  }

  function compareOp(aNum, bNum, op) {
    if (aNum === null || bNum === null) return false;
    if (op === "gt") return aNum > bNum;
    if (op === "gte") return aNum >= bNum;
    if (op === "lt") return aNum < bNum;
    if (op === "lte") return aNum <= bNum;
    if (op === "eq") return aNum === bNum;
    if (op === "neq") return aNum !== bNum;
    return false;
  }

  function pickBeneficial(code, nums, ignoreZeroCandidates) {
  const list = (Array.isArray(nums) ? nums : [])
    .filter((x) => typeof x === "number" && Number.isFinite(x))
    .filter((x) => (ignoreZeroCandidates ? x !== 0 : true));

  if (!list.length) return null;

  const dir = leverDirection(code);
  return dir === "higher" ? Math.max(...list) : Math.min(...list);
}

  function buildAutoAskPlan({ rules, overwriteExistingBA, ignoreZeroCandidates }) {
    const rs = Array.isArray(rules) ? rules : [];
    const visible = Array.isArray(filteredSortedRows) ? filteredSortedRows : [];

    const plan = []; // { unitId, metric, value }
    const perLever = {};

    for (const row of visible) {
      const unitId = row?.unit?.id;
      if (!Number.isFinite(Number(unitId))) continue;

      for (const r of rs) {
        const code = r?.leverCode;
        if (!code) continue;

        // If not overwriting, only fill blanks
        const currentBA = toNumLoose(scenarioValue(row, code, "ba"));
        if (!overwriteExistingBA && currentBA !== null) continue;

        const left = toNumLoose(scenarioValue(row, code, r?.leftScenario || "actual"));
        const right = toNumLoose(scenarioValue(row, code, r?.rightScenario || "assessor"));

        if (!compareOp(left, right, r?.op || "gt")) continue;

        let candidateNums = [];
        if ((r?.action || "better_of_compared") === "better_of_all") {
          candidateNums = [
            toNumLoose(scenarioValue(row, code, "assessor")),
            toNumLoose(scenarioValue(row, code, "actual")),
            toNumLoose(scenarioValue(row, code, "third_party")),
          ];
        } else {
          candidateNums = [left, right];
        }

        const chosen = pickBeneficial(code, candidateNums, ignoreZeroCandidates);
        if (chosen === null) continue;

        // If overwriting, avoid no-op writes
        if (overwriteExistingBA && currentBA !== null && chosen === currentBA) continue;

        plan.push({ unitId: Number(unitId), metric: code, value: chosen });

        perLever[code] = (perLever[code] || 0) + 1;

        // If multiple rules try to set the same lever for same row, last one wins.
        // We'll de-dupe later.
      }
    }

    // De-dupe by (unitId, metric) — last wins
    const map = new Map();
    for (const p of plan) map.set(`${p.unitId}::${p.metric}`, p);
    const deduped = Array.from(map.values());

    return { plan: deduped, perLever };
  }

  async function previewAutoAsk({ rules, overwriteExistingBA, ignoreZeroCandidates }) {
    const { plan, perLever } = buildAutoAskPlan({ rules, overwriteExistingBA, ignoreZeroCandidates });
    setAutoAskLastPreview({
      count: plan.length,
      perLever,
      sample: plan.slice(0, 10).map((x) => ({ unitId: x.unitId, lever: x.metric, value: x.value })),
    });
  }

  async function runAutoAsk({ rules, overwriteExistingBA, ignoreZeroCandidates }) {
    const { plan } = buildAutoAskPlan({ rules, overwriteExistingBA, ignoreZeroCandidates });

    if (!plan.length) {
      window.alert("No BA values would change for the currently visible rows.");
      return;
    }
	
	// Build undo plan BEFORE writing (captures previous BA values)
	const undoItems = plan.map((p) => {
	  const row = (Array.isArray(filteredSortedRows) ? filteredSortedRows : []).find((r) => r?.unit?.id === p.unitId);
	  const prevBA = toNumLoose(scenarioValue(row, p.metric, "ba")); // null if blank
	  return { unitId: p.unitId, metric: p.metric, prevBA };
	});

    const ok = window.confirm(
      `BA Auto Ask will write ${plan.length} BA values to the backend for the currently visible rows.\n\nContinue?`
    );
    if (!ok) return;

    const ty = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

    setAutoAskBusy(true);
    setAutoAskProgress({ done: 0, total: plan.length });

    // Light concurrency limiter (keeps API stable)
    const CONCURRENCY = 5;
    let idx = 0;
    let done = 0;

    const worker = async () => {
      while (true) {
        const i = idx++;
        if (i >= plan.length) return;

        const job = plan[i];
        const metric = job.metric;
        const def = metricDefFor(metric) || { code: metric, type: "number", unit: null };

        try {
          await OGDB2.upsertBAIDFact(job.unitId, {
            tax_year: ty,
            scenario: "ba",
            metric_code: metric,
            value_num: job.value,
            value_text: null,
            unit: def.unit || null,
            // updated_by omitted (BulkAnalysisPage does not receive email)
          });

          // Optimistic local update (same shape as inline edit)
          setRows((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const rowIdx = next.findIndex((r) => r?.unit?.id === job.unitId);
            if (rowIdx === -1) return prev;

            const r0 = next[rowIdx];
            const resolved0 = r0?.resolved || {};
            const metricRow = resolved0?.[metric] || {};
            const resolved1 = { ...resolved0, [metric]: { ...metricRow, ba: job.value } };
            next[rowIdx] = { ...r0, resolved: resolved1 };
            return next;
          });
        } catch (e) {
          // Keep going, but surface a message at the end.
          console.error("BA Auto Ask save failed:", job, e);
        } finally {
          done += 1;
          setAutoAskProgress({ done, total: plan.length });
        }
      }
    };

    try {
      const workers = Array.from({ length: CONCURRENCY }, () => worker());
      await Promise.all(workers);
      window.alert("BA Auto Ask complete. (Any failed saves are logged to the console.)");
	  saveAutoAskUndo({
	  createdAt: new Date().toISOString(),
	  count: undoItems.length,
	  items: undoItems,
	});
    } finally {
      setAutoAskBusy(false);
      setAutoAskProgress(null);
    }
  }

  function guessFirmFormatFromFilter() {
    const s = String(appraisalFirm || "").toLowerCase();
    if (s.includes("capitol")) return "capitol";
    if (s.includes("pritchard") || s.includes("abbott") || s.includes("p&a")) return "pritchard_abbott";
    return "capitol";
  }

  async function doExportLeaseSummary({ appraiser_format, only_beneficial_asks, beneficial_min_threshold_pct }) {
    try {
      // Export exactly the rows shown right now, in the current order.
      const unit_ids = (Array.isArray(rows) ? rows : [])
        .map((r) => r?.unit?.id)
        .filter((x) => Number.isFinite(Number(x)))
        .map((x) => Number(x));

      if (!unit_ids.length) {
        window.alert("No rows to export.");
        return;
      }

      const tax_year = Number.isFinite(Number(taxYear)) ? Number(taxYear) : null;

      const res = await OGDB2.exportLeaseSummaryXlsx({
	    unit_ids,
	    tax_year,
	    appraiser_format,
	    only_beneficial_asks,
	    beneficial_min_threshold_pct,
	  });

      const blob = new Blob([res?.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ogdb2_lease_summary_export_${appraiser_format}_${tax_year || "all"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setExportOpen(false);
    } catch (e) {
      window.alert(e?.response?.data?.detail || e?.message || "Export failed");
    }
  }
  
  // --- Header right-click menu ---
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, col }
  useEffect(() => {
    function close() {
      setCtxMenu(null);
    }
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  function openCtxMenu(e, col) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      col,
    });
  }
  
  // ---------- Monthly production pivot fetch ----------
  useEffect(() => {
    let cancelled = false;

    async function runMonthlyPivot() {
      if (!showMonthlyProd) return;

      const unitIds = (Array.isArray(rows) ? rows : [])
        .map((r) => r?.unit?.id)
        .filter((x) => Number.isFinite(Number(x)))
        .map((x) => Number(x));

      if (!unitIds.length) return;

      const py = Number.isFinite(Number(prodYear)) ? Number(prodYear) : null;
      if (!py) return;

      const idsKey = unitIds.join(",");

      // ✅ Include monthlyProdMode in the request key so switching
      // Oil ↔ Gas ↔ Both always forces a refetch.
      const reqKey = `${py}::${monthlyProdMode}`;

      // Skip only if (year + mode + ids) are identical
      if (
        monthlyPivotReqRef.current.key === reqKey &&
        monthlyPivotReqRef.current.ids === idsKey
      ) {
        return;
      }

      monthlyPivotReqRef.current = { key: reqKey, ids: idsKey };

      try {
        const res = await OGDB2.pivotBAIDMonthlyProduction({
          unit_ids: unitIds,
          prod_year: py,
          months: [1,2,3,4,5,6,7,8,9,10,11,12],
          include_oil: monthlyProdMode !== "gas",
          include_gas: monthlyProdMode !== "oil",
        });

        const byUnit = res?.data?.by_unit || {};

        if (cancelled) return;

        // Inject the pivot into each row so getCell() can sort/filter on pm.* keys
        setRows((prev) => {
          const next = Array.isArray(prev) ? prev.map((r) => {
            const id = r?.unit?.id;
            const mp =
              byUnit?.[String(id)] ||
              byUnit?.[Number(id)] ||
              {};
            return { ...r, monthly_pivot: mp, monthly_pivot_year: py };
          }) : prev;
          return next;
        });
      } catch (e) {
        console.error("Monthly production pivot failed:", e);
      }
    }

    runMonthlyPivot();
    return () => {
      cancelled = true;
    };
  }, [showMonthlyProd, monthlyProdMode, prodYear, rows]);

  // ---------- Registry metric codes (ALWAYS request all) ----------
  const registryMetricsUnique = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of OGDB2_METRICS || []) {
      const code = m?.code;
      if (!code) continue;
      if (seen.has(code)) continue;
      seen.add(code);
      out.push(m);
    }
    return out;
  }, []);

  const registryMetricCodes = useMemo(() => registryMetricsUnique.map((m) => m.code), [registryMetricsUnique]);

  const registryMetricsByGroup = useMemo(() => {
    const buckets = {
      production: [],
      expenses: [],
      price_deck: [],
      valuation: [],
      other: [],
    };
    for (const m of registryMetricsUnique) {
      const gk = preferredMetricGroupKey(m);
      buckets[gk].push(m);
    }
    return buckets;
  }, [registryMetricsUnique]);

  // ---------- Columns (definitions) ----------
  const IDENTITY_COLUMNS = useMemo(
    () => [
      { key: "unit.id", label: "BAID", type: "string" },
      { key: "unit.well_label", label: "Lease", type: "string" },
      { key: "unit.appraisal_firm", label: "Appraisal Firm", type: "string" },
    ],
    []
  );

  const FLAG_COLUMNS = useMemo(
    () => [
      { key: "f.loe_pct_over_actual", label: "LOE %Δ", type: "percent" },
      { key: "f.loe_overstated", label: "LOE Flag", type: "bool" },
      { key: "f.production_pct_vs_assessor", label: "Prod %Δ", type: "percent" },
      { key: "f.production_up", label: "Prod Flag", type: "bool" },
      { key: "f.oil_price_pct_vs_assessor", label: "Oil %Δ", type: "percent" },
      { key: "f.oil_price_up", label: "Oil Flag", type: "bool" },
    ],
    []
  );

  // Column UI state (persisted)
  const [colUI, setColUI] = useState(() => {
    const saved = readColumnUI();
    if (saved) return saved;

    // Default columns match the key levers shown in BAID Detail → Levers (DCF)
	const selectedMetricCodesDefault = [
	  "working_interest_pct",
	  "royalty_interest_pct",

	  "comparable_oil_start_rate",
	  "comparable_gas_start_rate",
	  "oil_decline_rate",
	  "gas_decline_rate",

	  "oil_price",
	  "gas_price",

	  "loe_basis",
	  "loe", // imported LOE (assessor)
	  "loe_comparable", // computed + BA override lever

	  "discount_rate",
	  "economic_life_years",
	  "net_equipment_value",
	].filter(Boolean);

    return {
	  identityKeys: IDENTITY_COLUMNS.map((c) => c.key),

	  // ✅ NEW: exactly which flag columns are allowed to render
	  flagsOn: true,
	  flagKeys: FLAG_COLUMNS.map((c) => c.key),

	  selectedMetricCodes: selectedMetricCodesDefault,
	  metricColumnKeys: buildMetricColumnKeysFromLegacy({
	    selectedMetricCodes: selectedMetricCodesDefault,
	    scenarios: { assessor: true, actual: true, ba: true },
	    showDeltas: true,
	    metricIndex: new Map((registryMetricsUnique || []).map((m) => [m.code, m])),
	  }),
	  scenarios: { assessor: true, actual: true, ba: true },
	  showDeltas: true,
	  deltaOverrides: {}, // only store explicit user overrides
	  
      columnOrder: [],     // explicit order of column keys (drag/drop)
      pinnedKeys: [],      // ✅ pinned (frozen) columns on the left
	};
  });
  
  // 🔥 Reset: remove legacy flags + any saved f.* columns (we're starting over)
  const strippedFlagsOnceRef = React.useRef(false);
  useEffect(() => {
    if (strippedFlagsOnceRef.current) return;
    strippedFlagsOnceRef.current = true;

    setColUI((prev) => {
      const next = { ...(prev || {}) };

      // Disable flags UI + clear selected flags
      next.flagsOn = false;
      next.flagKeys = [];

      // Remove any f.* columns from all places they can be persisted
      const metricKeys = Array.isArray(next.metricColumnKeys) ? next.metricColumnKeys : [];
      next.metricColumnKeys = metricKeys.filter((k) => !String(k || "").startsWith("f."));

      const order = Array.isArray(next.columnOrder) ? next.columnOrder : [];
      next.columnOrder = order.filter((k) => !String(k || "").startsWith("f."));

      const pins = Array.isArray(next.pinnedKeys) ? next.pinnedKeys : [];
      next.pinnedKeys = pins.filter((k) => !String(k || "").startsWith("f."));

      return next;
    });
  }, []);

  // persist columns UI
  useEffect(() => {
    writeColumnUI(colUI);
  }, [colUI]);
  
  // --- Load saved views from backend (fallback to localStorage if backend unavailable) ---
  useEffect(() => {
    let cancelled = false;

    async function loadServerViews() {
      try {
        const res = await OGDB2.listSavedViews();
        const serverViews = Array.isArray(res?.data) ? res.data : [];

        // serverViews are shaped like:
        // { id, name, is_shared, share_slug, owner_email, state, updated_at }
        // Keep UI compatible by normalizing to { name, state, ... }.
        const normalized = serverViews.map((v) => ({
          id: v.id,
          name: v.name,
          state: v.state || {},
          is_shared: !!v.is_shared,
          share_slug: v.share_slug,
          owner_email: v.owner_email,
          savedAt: v.updated_at || new Date().toISOString(),
        }));

        if (!cancelled) {
          setViews(normalized);
          writeViewsLocal(normalized); // cache for fallback
        }
      } catch {
        // fallback already loaded from local
      }
    }

    loadServerViews();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore last view + consume one-time Home navigation intents (MUST happen in this order)
  useEffect(() => {
    // 1) Read Home “open this view + autorun” intents FIRST
    try {
      const vid = localStorage.getItem(AUTOSELECT_VIEW_ID_KEY);
      if (vid && String(vid).trim()) {
        pendingAutoViewIdRef.current = String(vid).trim();
        localStorage.removeItem(AUTOSELECT_VIEW_ID_KEY);
      }

      const ar = localStorage.getItem(AUTORUN_ONCE_KEY);
      if (ar) {
        autorunPendingRef.current = true;
        localStorage.removeItem(AUTORUN_ONCE_KEY);
      }
    } catch {}

    // 2) Only apply LAST_VIEW if Home is NOT driving a specific saved view
    const last = readLastView();
    const hasPendingAutoView = !!pendingAutoViewIdRef.current;

    if (!hasPendingAutoView && last) {
      // Use the same logic as normal view loading so controls match
      applyViewState(last);

      // Optional: if Home set autorun without specifying a view id, run from the snapshot
      if (autorunPendingRef.current) {
        autorunPendingRef.current = false;
        queueMicrotask(() => run(last));
      }
    }

    // 3) Mark hydrated so persistence can start
    hydratedRef.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // ✅ If Home requested a specific view id, apply the EXACT snapshot Home stored (LAST_VIEW),
  // then run from that same snapshot. Do NOT trust v.state here (it can be stale/missing fields).
  useEffect(() => {
    const vid = pendingAutoViewIdRef.current;
    if (!vid) return;

    const v = findViewById(vid);
    if (!v) return; // views not loaded yet

    pendingAutoViewIdRef.current = null;

    // ✅ Always select the view in the dropdown
    setSelectedViewId(String(v.id || ""));

    // ✅ Use the exact snapshot that Home stored
    const last = readLastView();
    const snapshot = last && typeof last === "object" ? last : (v.state || {});

    // Populate controls/colUI/filters from the snapshot
	suppressPersistRef.current = true;
	autoViewAppliedRef.current = false;

	applyViewState(snapshot);

	// ✅ Allow persistence only after React has had a tick to apply state
	setTimeout(() => {
	  autoViewAppliedRef.current = true;
	  suppressPersistRef.current = false;
	}, 0);

    // Run from the same snapshot so results match what you clicked
    if (autorunPendingRef.current) {
      autorunPendingRef.current = false;
      queueMicrotask(() => run(snapshot));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views]);
  
  // ✅ Auto-run once (only when Home clicked a saved view)
  useEffect(() => {
    if (!autorunPendingRef.current) return;
	if (!hydratedRef.current) return;
    if (busy) return;
	
	if (selectedViewId && autoViewAppliedRef.current === false) return;

    // wait until React has applied restored state
    autorunPendingRef.current = false;
    setTimeout(() => {
      run();
    }, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, taxYear, limit, q, county, operator, appraisalFirm, rrc, leaseName, accountId, interestType]);

  // Persist last view continuously
  useEffect(() => {
	if (!hydratedRef.current) return;

	// ✅ If Home is in the middle of driving a saved-view open, don't clobber LAST_VIEW.
	// This prevents localhost/dev (StrictMode + async view load) from overwriting the clicked snapshot.
	if (suppressPersistRef.current || pendingAutoViewIdRef.current || autorunPendingRef.current || autoViewAppliedRef.current === false) {
	  return;
	}
    writeLastView({
      client,
      taxYear,
      limit,
      q,
      county,
      operator,
      appraisalFirm,
      rrc,
      leaseName,
      accountId,
      interestType,
      hideEmptyMetricCols,

      // ✅ Monthly production controls (last view)
      showMonthlyProd,
      monthlyProdMode,
      prodYear,

      sort,
      colFilters,
	  colUI, // ✅ persist columns config too
    });
  }, [
    client,
    taxYear,
    limit,
    q,
    county,
    operator,
    appraisalFirm,
    rrc,
    leaseName,
    accountId,
    interestType,
    hideEmptyMetricCols,

    // ✅ Monthly production controls
    showMonthlyProd,
    monthlyProdMode,
    prodYear,

    sort,
    colFilters,
	colUI,
  ]);

  // ---------- Build columns based on modal config ----------
  const metricIndex = useMemo(() => {
    const map = new Map();
    for (const m of registryMetricsUnique || []) {
      if (m?.code) map.set(m.code, m);
    }
    return map;
  }, [registryMetricsUnique]);
  
  // ✅ One-time migration: if metricColumnKeys is missing/empty, build it from legacy fields
  useEffect(() => {
    const hasKeys = Array.isArray(colUI?.metricColumnKeys) && colUI.metricColumnKeys.length > 0;
    if (hasKeys) return;

    const legacyCodes = Array.isArray(colUI?.selectedMetricCodes) ? colUI.selectedMetricCodes : [];
    const legacyScenarios = colUI?.scenarios || { assessor: true, actual: true, third_party: false, ba: true };
    const legacyShowDeltas = colUI?.showDeltas !== false;

    const nextKeys = buildMetricColumnKeysFromLegacy({
      selectedMetricCodes: legacyCodes,
      scenarios: legacyScenarios,
      showDeltas: legacyShowDeltas,
      metricIndex,
    });

    if (!nextKeys.length) return;

    setColUI((prev) => ({
      ...(prev || {}),
      metricColumnKeys: nextKeys,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricIndex]);

  function isNegotiableForDelta(code) {
    // explicit override wins
    const overrides = colUI?.deltaOverrides || {};
    if (Object.prototype.hasOwnProperty.call(overrides, code)) return !!overrides[code];

    // otherwise default: only allow deltas if NOT objective and in negotiable buckets
    if (DEFAULT_OBJECTIVE_CODES.has(code)) return false;

    const def = metricIndex.get(code);
    const cats = def?.categories || [];
    const hasNegotiableCategory =
      cats.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION) ||
      cats.includes(OGDB2_METRIC_CATEGORIES.EXPENSES) ||
      cats.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK) ||
      cats.includes(OGDB2_METRIC_CATEGORIES.VALUATION);

    return !!hasNegotiableCategory;
  }

  function buildMetricColsForCodes(codes) {
    // Back-compat: old system (metric codes + global scenarios + deltas)
    const cols = [];
    const scenarios = colUI?.scenarios || { assessor: true, actual: true, ba: true };
    const showDeltas = colUI?.showDeltas !== false;

    for (const code of codes || []) {
      if (!code) continue;
      const def = metricIndex.get(code);
      const label = def?.label || code;

      if (scenarios.assessor) cols.push({ key: `m.${code}.assessor`, label: `${label} (A)`, type: "number" });
      if (scenarios.actual) cols.push({ key: `m.${code}.actual`, label: `${label} (Act)`, type: "number" });
      if (scenarios.ba) cols.push({ key: `m.${code}.ba`, label: `${label} (BA)`, type: "number" });

      if (showDeltas && scenarios.assessor && isNegotiableForDelta(code)) {
        if (scenarios.actual) {
          cols.push({ key: `d.${code}.act_abs`, label: `${label} Δ(Act-A)`, type: "number" });
          cols.push({ key: `d.${code}.act_pct`, label: `${label} %Δ(Act-A)`, type: "percent" });
        }
        if (scenarios.ba) {
          cols.push({ key: `d.${code}.ba_abs`, label: `${label} Δ(BA-A)`, type: "number" });
          cols.push({ key: `d.${code}.ba_pct`, label: `${label} %Δ(BA-A)`, type: "percent" });
        }
      }
    }
    return cols;
  }

  function buildMetricColsFromKeys(keys) {
    const cols = [];
    const list = Array.isArray(keys) ? keys : [];

    for (const k of list) {
      if (!k || typeof k !== "string") continue;

      // metrics
      if (k.startsWith("m.")) {
        const parts = k.split(".");
        const code = parts[1];
        const scenario = parts[2];
        const def = metricIndex.get(code);
        const label = def?.label || code;
        const suffix =
          scenario === "assessor" ? "(A)" :
          scenario === "actual" ? "(Act)" :
          scenario === "third_party" ? "(3P)" :
          "(BA)";
        cols.push({ key: k, label: `${label} ${suffix}`, type: "number" });
        continue;
      }

      // deltas
      if (k.startsWith("d.")) {
        const parts = k.split(".");
        const code = parts[1];
        const kind = parts[2];
        const def = metricIndex.get(code);
        const label = def?.label || code;

        if (kind === "act_abs") cols.push({ key: k, label: `${label} Δ(Act-A)`, type: "number" });
        else if (kind === "act_pct") cols.push({ key: k, label: `${label} %Δ(Act-A)`, type: "percent" });
        else if (kind === "ba_abs") cols.push({ key: k, label: `${label} Δ(BA-A)`, type: "number" });
        else if (kind === "ba_pct") cols.push({ key: k, label: `${label} %Δ(BA-A)`, type: "percent" });
        else cols.push({ key: k, label: `${label} ${kind}`, type: "number" });
        continue;
      }
    }

    // de-dupe by key
    const seen = new Set();
    return cols.filter((c) => {
      if (!c?.key) return false;
      if (seen.has(c.key)) return false;
      seen.add(c.key);
      return true;
    });
  }

  const ALL_COLUMNS = useMemo(() => {
    const seen = new Set();
    const out = [];

    const identityKeys = Array.isArray(colUI?.identityKeys) ? colUI.identityKeys : IDENTITY_COLUMNS.map((c) => c.key);
    const flagsOn = colUI?.flagsOn !== false;

    // identity
    for (const c of IDENTITY_COLUMNS) {
      if (!identityKeys.includes(c.key)) continue;
      if (seen.has(c.key)) continue;
      seen.add(c.key);
      out.push(c);
    }

    // flags (dictated by Columns popup — NO exceptions)
	if (flagsOn) {
	  const allowedFlagKeys = new Set(
		Array.isArray(colUI?.flagKeys) && colUI.flagKeys.length
		  ? colUI.flagKeys
		  : FLAG_COLUMNS.map((c) => c.key) // backward-compatible if old localStorage config exists
	  );

	  for (const c of FLAG_COLUMNS) {
		if (!c?.key) continue;
		if (!allowedFlagKeys.has(c.key)) continue;
		if (seen.has(c.key)) continue;
		seen.add(c.key);
		out.push(c);
	  }
	}

    // metrics
    const metricKeysRaw = Array.isArray(colUI?.metricColumnKeys) ? colUI.metricColumnKeys : null;
	const metricKeys = metricKeysRaw
	  ? metricKeysRaw.filter((k) => (showThirdParty ? true : !String(k).endsWith(".third_party")))
	  : null;

    if (metricKeys && metricKeys.length) {
      for (const c of buildMetricColsFromKeys(metricKeys)) {
        if (!c?.key) continue;
        if (seen.has(c.key)) continue;
        seen.add(c.key);
        out.push(c);
      }
    } else {
      // backward compat: old selectedMetricCodes + scenarios
      const codes = Array.isArray(colUI?.selectedMetricCodes) ? colUI.selectedMetricCodes : [];
      for (const c of buildMetricColsForCodes(codes)) {
        if (!c?.key) continue;
        if (seen.has(c.key)) continue;
        seen.add(c.key);
        out.push(c);
      }
    }
	
	// monthly production columns (optional)
    if (showMonthlyProd) {
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

      if (monthlyProdMode !== "gas") {
        for (let i = 0; i < 12; i++) {
          const mm = String(i + 1).padStart(2, "0");
          out.push({ key: `pm.oil_${mm}`, label: `Oil ${monthNames[i]}`, type: "number" });
        }
      }

      if (monthlyProdMode !== "oil") {
        for (let i = 0; i < 12; i++) {
          const mm = String(i + 1).padStart(2, "0");
          out.push({ key: `pm.gas_${mm}`, label: `Gas ${monthNames[i]}`, type: "number" });
        }
      }
    }

    // Apply drag order (persisted)
    let out2 = out;

    const orderRaw = Array.isArray(colUI?.columnOrder) ? colUI.columnOrder : [];
	if (orderRaw.length) {
	  const normalized = normalizeColumnOrder(orderRaw, out2.map((x) => x.key));
	  const pos = new Map(normalized.map((k, i) => [k, i]));

	  out2 = [...out2].sort((a, b) => {
		const ai = pos.has(a.key) ? pos.get(a.key) : 1e9;
		const bi = pos.has(b.key) ? pos.get(b.key) : 1e9;
		if (ai !== bi) return ai - bi;
		return String(a.key).localeCompare(String(b.key));
	  });
	}

    return out2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colUI, IDENTITY_COLUMNS, FLAG_COLUMNS, metricIndex, showThirdParty, showMonthlyProd, monthlyProdMode, prodYear]);

  // Optional auto-prune empty metric columns after a run
  const visibleColumns = useMemo(() => {
    if (!hideEmptyMetricCols || !hasRun) return ALL_COLUMNS;

    const keep = [];
    for (const col of ALL_COLUMNS) {
      if (col.key.startsWith("unit.") || col.key.startsWith("f.")) {
        keep.push(col);
        continue;
      }

      // metric/delta columns: keep if any row has a value
      let any = false;
      for (const r of rows || []) {
        const v = getCell(r, col.key);
        if (v !== null && v !== undefined && v !== "") {
          any = true;
          break;
        }
      }
      if (any) keep.push(col);
    }
    return keep;
  }, [ALL_COLUMNS, hideEmptyMetricCols, hasRun, rows]);
  
  // ------------------------------------------------------------
  // Pinned columns (freeze left) + header right-click menu
  

  function toggleSort(colKey) {
    setSort((prev) => {
      if (prev?.key !== colKey) return { key: colKey, dir: "asc" };
      return { key: colKey, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }

  function openFilterPrompt(col) {
    const key = col.key;
    const type = col.type;

    if (type === "bool") {
      const choice = window.prompt(`Filter ${col.label}:\nType "true" or "false" (or blank to clear)`, "");
      if (!choice) {
        setColFilters((p) => {
          const n = { ...(p || {}) };
          delete n[key];
          return n;
        });
        return;
      }
      const c = choice.trim().toLowerCase();
      const op = c === "true" ? "is_true" : "is_false";
      setColFilters((p) => ({ ...(p || {}), [key]: { op, value: "" } }));
      return;
    }

    if (type === "number" || type === "percent") {
      const choice = window.prompt(
        `Filter ${col.label}:\nExamples:\n> 100000\n>= 0\n< 10\n<= 5\n= 123\n(blank to clear)`,
        ""
      );
      if (!choice) {
        setColFilters((p) => {
          const n = { ...(p || {}) };
          delete n[key];
          return n;
        });
        return;
      }
      const s = choice.trim();
      let op = "gte";
      let val = s;

      if (s.startsWith(">=")) {
        op = "gte";
        val = s.slice(2).trim();
      } else if (s.startsWith("<=")) {
        op = "lte";
        val = s.slice(2).trim();
      } else if (s.startsWith(">")) {
        op = "gt";
        val = s.slice(1).trim();
      } else if (s.startsWith("<")) {
        op = "lt";
        val = s.slice(1).trim();
      } else if (s.startsWith("=")) {
        op = "eq_num";
        val = s.slice(1).trim();
      }

      setColFilters((p) => ({ ...(p || {}), [key]: { op, value: val } }));
      return;
    }

    const choice = window.prompt(`Filter ${col.label} contains (blank to clear):`, "");
    if (!choice) {
      setColFilters((p) => {
        const n = { ...(p || {}) };
        delete n[key];
        return n;
      });
      return;
    }
    setColFilters((p) => ({ ...(p || {}), [key]: { op: "contains", value: choice } }));
  }

  const filteredSortedRows = useMemo(() => {
    let out = Array.isArray(rows) ? [...rows] : [];

    for (const [colKey, f] of Object.entries(colFilters || {})) {
      if (!f) continue;
      out = out.filter((r) => applyFilter(getCell(r, colKey), f));
    }

    if (sort?.key) {
      out.sort((a, b) => compareValues(getCell(a, sort.key), getCell(b, sort.key), sort.dir));
    }
    return out;
  }, [rows, colFilters, sort]);
  
  // ---------- Heatmap (professional + subtle) ----------
  const heatmapOn = !!colUI?.heatmapOn;

  // Determines whether a delta is favorable/unfavorable given the lever direction.
  // "lower" => negative delta is favorable (green), positive is unfavorable (red)
  // "higher" => positive delta is favorable (green), negative is unfavorable (red)
  function heatmapSignTone(metricCode, deltaValue) {
    const v = typeof deltaValue === "number" ? deltaValue : null;
    if (v === null) return null;

    const dir = leverDirection(metricCode); // "lower" | "higher"
    const favorable = dir === "higher" ? v > 0 : v < 0;
    const unfavorable = dir === "higher" ? v < 0 : v > 0;

    if (favorable) return "good";
    if (unfavorable) return "bad";
    return "neutral";
  }

  // Compute per-column max abs so each column normalizes its own intensity.
  const heatmapMaxAbsByCol = useMemo(() => {
    if (!heatmapOn) return {};

    const cols = Array.isArray(visibleColumns) ? visibleColumns : [];
    const out = {};

    for (const c of cols) {
      const key = c?.key;
      if (!key || !String(key).startsWith("d.")) continue; // deltas only
      let maxAbs = 0;

      for (const r of filteredSortedRows || []) {
        const val = getCell(r, key);
        const n = typeof val === "number" ? val : Number(String(val ?? "").replace(/,/g, ""));
        if (!Number.isFinite(n)) continue;
        const a = Math.abs(n);
        if (a > maxAbs) maxAbs = a;
      }

      out[key] = maxAbs;
    }

    return out;
  }, [heatmapOn, visibleColumns, filteredSortedRows]);

  function heatmapStyleForCell(colKey, value) {
    if (!heatmapOn) return null;
    if (!colKey || !String(colKey).startsWith("d.")) return null;

    const n = typeof value === "number" ? value : Number(String(value ?? "").replace(/,/g, ""));
    if (!Number.isFinite(n) || n === 0) return null;

    // Parse metric code from delta key: d.<code>.<kind>
    const parts = String(colKey).split(".");
    const metricCode = parts?.[1] || "";

    const maxAbs = heatmapMaxAbsByCol?.[colKey] || 0;
    if (!maxAbs || !Number.isFinite(maxAbs)) return null;

    // normalized 0..1
    const t = Math.min(1, Math.abs(n) / maxAbs);

    // opacity ramp: subtle base + gentle scale (professional)
    const alpha = Math.min(0.18, 0.04 + 0.14 * t);

    const tone = heatmapSignTone(metricCode, n);

    if (tone === "good") return { backgroundColor: `rgba(16, 185, 129, ${alpha})` }; // muted emerald
    if (tone === "bad") return { backgroundColor: `rgba(244, 63, 94, ${alpha})` };   // muted rose
    return null;
  }

  async function run(stateOverride = null) {
    // Build the query from an explicit snapshot (prevents race conditions with setState timing)
    const s = stateOverride && typeof stateOverride === "object" ? stateOverride : {};

    const snap = {
      client: (s.client ?? client) || "",
      taxYear: String(s.taxYear ?? taxYear ?? "").trim(),
      limit: s.limit ?? limit,
      q: (s.q ?? q) || "",
      county: (s.county ?? county) || "",
      operator: (s.operator ?? operator) || "",
      appraisalFirm: (s.appraisalFirm ?? appraisalFirm) || "",
      rrc: (s.rrc ?? rrc) || "",
	  leaseNumbers: Array.isArray(s.leaseNumbers) ? s.leaseNumbers : (Array.isArray(leaseNumbers) ? leaseNumbers : []),
      leaseName: (s.leaseName ?? leaseName) || "",
      accountId: (s.accountId ?? accountId) || "",
      interestType: (s.interestType ?? interestType) || "",
    };

    const hasAnySearch =
      String(snap.client).trim() ||
      String(snap.taxYear).trim() ||
      String(snap.q).trim() ||
      String(snap.county).trim() ||
      String(snap.operator).trim() ||
      String(snap.appraisalFirm).trim() ||
      String(snap.rrc).trim() ||
	  (Array.isArray(snap.leaseNumbers) && snap.leaseNumbers.length) ||
      String(snap.leaseName).trim() ||
      String(snap.accountId).trim() ||
      String(snap.interestType).trim();

    if (!hasAnySearch) {
      setErr("Enter at least one search field (client, year, lease name, RRC, etc.).");
      return;
    }

    setBusy(true);
    setErr("");
    setHasRun(true);

    try {
      const filters = [];

      if (snap.client.trim()) filters.push({ type: "field", field: "client", op: "contains", value: snap.client.trim() });
      if (snap.county.trim()) filters.push({ type: "field", field: "county", op: "contains", value: snap.county.trim() });
      if (snap.operator.trim()) filters.push({ type: "field", field: "operator", op: "contains", value: snap.operator.trim() });
      if (snap.appraisalFirm.trim()) filters.push({ type: "field", field: "appraisal_firm", op: "contains", value: snap.appraisalFirm.trim() });
      // If tokens exist, we do OR behavior client-side (multiple requests + merge).
	  // If no tokens, we keep the single-field filter behavior exactly as before.
	  const tokensRaw = Array.isArray(snap.leaseNumbers) ? snap.leaseNumbers : [];
	  const tokens = tokensRaw
	    .map((x) => String(x || "").replace(/[^\d]/g, "").trim())
	    .filter(Boolean);

	  if (!tokens.length && snap.rrc.trim()) {
	    filters.push({ type: "field", field: "rrc_number", op: "contains", value: snap.rrc.trim() });
	  }
      if (snap.leaseName.trim()) filters.push({ type: "field", field: "lease_name", op: "contains", value: snap.leaseName.trim() });
      if (snap.accountId.trim()) filters.push({ type: "field", field: "external_account_id", op: "contains", value: snap.accountId.trim() });
      if (snap.interestType.trim()) filters.push({ type: "field", field: "interest_type", op: "contains", value: snap.interestType.trim() });
	  
	  // --- Token OR-search (frontend batching) ---
	  // If tokens exist, perform multiple queries (one per token) and merge results by unit.id
	  if (tokens.length) {
	    const basePayload = (extraFilters) => ({
		  search: {
		    q: snap.q.trim() || null,
		    limit: 200, // per-token cap; merged result can exceed this
		    offset: 0,
		    sort: "updated_at_desc",
		    filters: extraFilters,
		  },
		  metric_codes: registryMetricCodes,
		  tax_year: Number.isFinite(Number(snap.taxYear)) ? Number(snap.taxYear) : null,
	    });

	    const merged = new Map(); // unit.id -> row

	    // Run sequentially to avoid hammering the API. (We can add concurrency later.)
	    for (let i = 0; i < tokens.length; i++) {
		  const t = tokens[i];

		  const res = await OGDB2.bulkQueryBAID(
		    basePayload([
			  ...filters,
			  { type: "field", field: "rrc_number", op: "contains", value: t },
		    ])
		  );

		  const items = res?.data?.items || [];
		  for (const row of items) {
		    const id = row?.unit?.id;
		    if (!id) continue;
		    if (!merged.has(id)) merged.set(id, row);
		  }
	    }

	    setRows(Array.from(merged.values()));
	    return;
	  }

      const payload = {
        search: {
          q: snap.q.trim() || null,
          limit: Number.isFinite(Number(snap.limit)) ? Number(snap.limit) : 200,
          offset: 0,
          sort: "updated_at_desc",
          filters,
        },

        metric_codes: registryMetricCodes,
        tax_year: Number.isFinite(Number(snap.taxYear)) ? Number(snap.taxYear) : null,
      };

      const res = await OGDB2.bulkQueryBAID(payload);
      const items = res?.data?.items || [];
      setRows(items);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    const cols = visibleColumns;
    const lines = [];
    lines.push(cols.map((c) => csvEscape(c.label)).join(","));

    for (const r of filteredSortedRows) {
      lines.push(
        cols
          .map((c) => {
            const v = getCell(r, c.key);

            // Ensure identifier-like fields never include thousands separators
            if (c.key === "unit.id" || c.key.includes("m.lease_number.")) {
              return csvEscape(v === null || v === undefined ? "" : String(v).replace(/,/g, ""));
            }

            if (c.type === "bool") return csvEscape(Boolean(v) ? "✅" : "");
            if (c.type === "percent") return csvEscape(fmtPct(v));

            return csvEscape(fmt(v));
          })
          .join(",")
      );
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `ogdb2_bulk_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function saveView() {
    const name = window.prompt("Name this view:", "");
    if (!name) return;

    const share = window.confirm("Share this view with other users?");

    const nextState = {
	  client,
	  taxYear,
	  limit,
	  q,
	  county,
	  operator,
	  appraisalFirm,
	  rrc,
	  leaseName,
	  leaseNumbers,
	  accountId,
	  interestType,

	  hideEmptyMetricCols,

      // ✅ Monthly production controls (saved view)
      showMonthlyProd,
      monthlyProdMode,
      prodYear,

	  sort,
	  colFilters,

	  // ✅ this is what actually controls columns/scenarios/deltas now
	  colUI,
	};

    try {
      // Upsert in backend (per-user unique by name)
      await OGDB2.upsertSavedView({
        name: String(name).trim(),
        is_shared: !!share,
        state: nextState,
      });

      // Re-fetch from backend for canonical ordering/ids
      const res = await OGDB2.listSavedViews();
      const serverViews = Array.isArray(res?.data) ? res.data : [];
      const normalized = serverViews.map((v) => ({
        id: v.id,
        name: v.name,
        state: v.state || {},
        is_shared: !!v.is_shared,
        share_slug: v.share_slug,
        owner_email: v.owner_email,
        savedAt: v.updated_at || new Date().toISOString(),
      }));

      setViews(normalized);
      writeViewsLocal(normalized);
	  // Select the newly saved view by id
      const saved = normalized.find((x) => x.name === String(name).trim() && x.owner_email);
      if (saved?.id) setSelectedViewId(String(saved.id));
    } catch (e) {
      // If backend fails, fall back to local save (old behavior)
      const fallbackView = {
        id: undefined,
        name: String(name).trim(),
        savedAt: new Date().toISOString(),
        state: nextState,
        is_shared: false,
        share_slug: null,
        owner_email: null,
      };

      const current = readViewsLocal();
      const withoutSame = current.filter((v) => v?.name !== fallbackView.name);
      const merged = [fallbackView, ...withoutSame];
      writeViewsLocal(merged);
      setViews(merged);

      window.alert(e?.response?.data?.detail || e?.message || "Saved locally (server save failed).");
    }
  }
  
  function applyViewState(s) {
      const st = s && typeof s === "object" ? s : {};

      setClient(st.client ?? "");
      setTaxYear(String(st.taxYear ?? yearNow));
      setLimit(st.limit ?? 200);

      setQ(st.q ?? "");
      setCounty(st.county ?? "");
      setOperator(st.operator ?? "");
      setAppraisalFirm(st.appraisalFirm ?? "");
      setRrc(st.rrc ?? "");
	  setLeaseNumbers(Array.isArray(st.leaseNumbers) ? st.leaseNumbers : []);
      setLeaseName(st.leaseName ?? "");
      setAccountId(st.accountId ?? "");
      setInterestType(st.interestType ?? "");

      setHideEmptyMetricCols(st.hideEmptyMetricCols ?? true);

      // ✅ Monthly production controls (restore)
      const smp = !!st.showMonthlyProd;
      setShowMonthlyProd(smp);
      setMonthlyProdMode(st.monthlyProdMode || "both");

      // prodYear: restore if present; else default to (taxYear - 1)
      const tyNum = Number.isFinite(Number(st.taxYear ?? yearNow)) ? Number(st.taxYear ?? yearNow) : yearNow;
      const defaultProdYear = String(tyNum - 1);
      setProdYear(String(st.prodYear ?? defaultProdYear));

      setSort(st.sort || { key: "unit.well_label", dir: "asc" });

      // IMPORTANT: restore filters too
      setColFilters(st.colFilters && typeof st.colFilters === "object" ? st.colFilters : {});

      if (st.colUI && typeof st.colUI === "object") {
        setColUI(st.colUI);
      }
  }

  function loadView(v) {
      if (!v) return;
      applyViewState(v.state || {});
      setSelectedViewId(String(v.id || ""));
      // ✅ Delay marking “applied” until next tick so React state updates land
	  // before the persistence effect is allowed to write LAST_VIEW.
	  setTimeout(() => {
	    autoViewAppliedRef.current = true;
	  }, 0);
  }

  async function deleteViewById(viewId) {
    const target = findViewById(viewId);
    if (!target) return;

    if (!window.confirm(`Delete view "${target.name}"?`)) return;

    // If it has an id, delete on server
    if (target?.id) {
      try {
        await OGDB2.deleteSavedView(target.id);

        const res = await OGDB2.listSavedViews();
        const serverViews = Array.isArray(res?.data) ? res.data : [];
        const normalized = serverViews.map((v) => ({
          id: v.id,
          name: v.name,
          state: v.state || {},
          is_shared: !!v.is_shared,
          share_slug: v.share_slug,
          owner_email: v.owner_email,
          savedAt: v.updated_at || new Date().toISOString(),
        }));

        setViews(normalized);
        writeViewsLocal(normalized);
        setSelectedViewId("");
        return;
      } catch (e) {
        window.alert(e?.response?.data?.detail || e?.message || "Server delete failed.");
      }
    }
  }

  return (
    <div className="space-y-4">
      <BulkAnalysisColumnsModal
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        registryMetricsUnique={registryMetricsUnique}
        registryMetricsByGroup={registryMetricsByGroup}
        identityColumns={IDENTITY_COLUMNS}
        flagColumns={FLAG_COLUMNS}
        value={colUI}
        onChange={setColUI}
      />
	  
	  <ExportLeaseSummaryModal
	    open={exportOpen}
	    onClose={() => setExportOpen(false)}
	    defaultFirmFormat={guessFirmFormatFromFilter()}
	    defaultOnlyBeneficial={true}
	    onExport={doExportLeaseSummary}
	  />
	  
	  <BulkAnalysisAutoAskModal
	    open={autoAskOpen}
	    onClose={() => setAutoAskOpen(false)}
	    levers={BA_AUTO_ASK_LEVERS}
	    onPreview={previewAutoAsk}
	    onRun={runAutoAsk}
	    busy={autoAskBusy}
	    progress={autoAskProgress}
	    lastPreview={autoAskLastPreview}
	    canUndo={Boolean(autoAskUndo?.items?.length)}
	    onUndo={runUndoLastAutoAsk}
	    undoMeta={
		  autoAskUndo?.createdAt
		    ? { createdAt: autoAskUndo.createdAt, count: autoAskUndo.items?.length || 0 }
		    : null
	    }
	  />
	  
	  <DeclineAsksModal
        open={declineAsksOpen}
        onClose={() => setDeclineAsksOpen(false)}
        rows={filteredSortedRows}
        taxYear={taxYear}
        getCell={getCell}
        onApplyChanges={applyDeclineAsks}
      />

	  <MassLOEBasisModal
        open={massBasisOpen}
        onClose={() => setMassBasisOpen(false)}
        rows={filteredSortedRows}
        getCell={getCell}
        onApply={applyMassLOEBasis}
      />

	  <ProductionDetailPopupWindow
	    open={prodPopupOpen}
	    onClose={() => setProdPopupOpen(false)}
	    unitId={prodPopupUnitId}
	    prodYear={prodYear}
	    resolvedFacts={prodPopupResolved}
	    defaultTab={monthlyProdMode === "gas" ? "gas" : "oil"}
	    oilBasis={prodPopupResolved?.tp_start_rate_basis_oil?.ba || ""}
	    gasBasis={prodPopupResolved?.tp_start_rate_basis_gas?.ba || ""}
	    onSetOilBasis={(b) => setBasisForUnit(prodPopupUnitId, "tp_start_rate_basis_oil", b)}
	    onSetGasBasis={(b) => setBasisForUnit(prodPopupUnitId, "tp_start_rate_basis_gas", b)}
	  />

      {/* Header */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold text-gray-900">Bulk Analysis</div>
          <div className="text-xs text-gray-500">
            Columns are identity + flags + canonical metrics (Assessor / Actual / BA) + deltas only where negotiable.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button className="btn" onClick={onBack} type="button">
            Back
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {String(err)}
        </div>
      )}

      {/* Controls */}
      <Section
        title="Controls"
        right={
          <div className="flex flex-wrap items-center gap-2">		
					  <button
					    className="btn"
					    onClick={() => setColumnsOpen(true)}
					    type="button"
					  >
					    Columns
					  </button>

					  <button className="btn btn-primary" type="button" onClick={() => setExportOpen(true)}>
					    Export Excel…
					  </button>
					  
					  <button className="btn btn-primary" type="button" onClick={() => setDeclineAsksOpen(true)}>
						Decline Asks
					  </button>

					  <button className="btn btn-primary" type="button" onClick={() => setAutoAskOpen(true)}>
					    BA Auto Ask
					  </button>

					  <button className="btn btn-primary" type="button" onClick={() => setMassBasisOpen(true)}>
					    Mass LOE Basis
					  </button>

					  <button
					    className="btn"
					    type="button"
					    onClick={() => {
						  setProdPopupOpen(true);

						  // If nothing selected yet, default to the first visible row for convenience
						  const first = (Array.isArray(filteredSortedRows) ? filteredSortedRows : [])[0];
						  const uid = first?.unit?.id;
						  if (uid) {
						    setProdPopupUnitId(uid);
						    setProdPopupResolved(first?.resolved || {});
						  }
					    }}
					  >
					    Production Detail ↗
					  </button>

            <button className="btn" onClick={saveView} type="button">
              Save View
            </button>

            <select
              className="rounded-xl border border-gray-200 px-3 py-2 bg-white"
              value={selectedViewId}
              onChange={(e) => {
                const id = e.target.value;
                setSelectedViewId(id);
                if (id) {
                  const v = findViewById(id);
                  if (v) loadView(v);
                }
              }}
            >
              <option value="">Load view…</option>
              {views
                .filter((v) => !!v?.id)
                .map((v) => (
                  <option key={String(v.id)} value={String(v.id)}>
                    {v.name}
                    {v.is_shared ? " (Shared)" : ""}
                  </option>
                ))}
            </select>

            {selectedViewId ? (
              <button className="btn" onClick={() => deleteViewById(selectedViewId)} type="button">
                Delete View
              </button>
            ) : null}

            <button className="btn-primary" disabled={busy} onClick={run} type="button">
              {busy ? "Running…" : "Run"}
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Client</label>
            <input
              className="w-full rounded-xl border border-gray-200 px-3 py-2"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              placeholder="e.g., Eagle Ridge"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Appraisal Firm</label>

			<div className="flex gap-2">
			  <select
				className="w-full rounded-xl border border-gray-200 px-3 py-2 bg-white"
				value={APPRAISAL_FIRM_OPTIONS.some((x) => x.value === appraisalFirm) ? appraisalFirm : "__CUSTOM__"}
				onChange={(e) => {
				  const v = e.target.value;
				  if (v === "__CUSTOM__") {
					// keep existing text so user can edit it below
					if (APPRAISAL_FIRM_OPTIONS.some((x) => x.value === appraisalFirm)) setAppraisalFirm("");
				  } else {
					setAppraisalFirm(v);
				  }
				}}
			  >
				{APPRAISAL_FIRM_OPTIONS.map((o) => (
				  <option key={o.value} value={o.value}>
					{o.label}
				  </option>
				))}
			  </select>

			  {(!APPRAISAL_FIRM_OPTIONS.some((x) => x.value === appraisalFirm) || appraisalFirm === "") && (
				<input
				  className="w-[260px] rounded-xl border border-gray-200 px-3 py-2"
				  value={APPRAISAL_FIRM_OPTIONS.some((x) => x.value === appraisalFirm) ? "" : appraisalFirm}
				  onChange={(e) => setAppraisalFirm(e.target.value)}
				  placeholder="Type firm…"
				/>
			  )}
			</div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Search (q)</label>
            <input
              className="w-full rounded-xl border border-gray-200 px-3 py-2"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="rrc, lease, operator, account id..."
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs text-gray-500 mb-1">Tax Year</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={taxYear} onChange={(e) => setTaxYear(e.target.value)} />
          </div>

          <div className="md:col-span-1">
            <label className="block text-xs text-gray-500 mb-1">Limit</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">County</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={county} onChange={(e) => setCounty(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Operator</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={operator} onChange={(e) => setOperator(e.target.value)} />
          </div>

          <div className="md:col-span-2">
		    <MultiTokenInput
			  label="Lease Number (paste list OK)"
			  placeholder="Type a lease/RRC number, or paste a column from Excel…"
			  tokens={leaseNumbers}
			  onTokensChange={setLeaseNumbers}
			  text={rrc}
			  onTextChange={setRrc}
			  suggestions={leaseSuggestions}
			  disabled={busy}
		    />
		  </div>

          <div className="md:col-span-1">
            <label className="block text-xs text-gray-500 mb-1">Interest Type</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={interestType} onChange={(e) => setInterestType(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Lease Name</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={leaseName} onChange={(e) => setLeaseName(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Account ID</label>
            <input className="w-full rounded-xl border border-gray-200 px-3 py-2" value={accountId} onChange={(e) => setAccountId(e.target.value)} />
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={hideEmptyMetricCols} onChange={(e) => setHideEmptyMetricCols(e.target.checked)} />
              Hide empty metric columns
            </label>
			
			<label className="flex items-center gap-2 text-sm text-gray-700">
			  <input
				type="checkbox"
				checked={!!colUI?.heatmapOn}
				onChange={(e) => {
				  const on = !!e.target.checked;
				  setColUI((prev) => ({ ...(prev || {}), heatmapOn: on }));
				}}
			  />
			  Heatmap
			</label>
			
			<label className="flex items-center gap-2 text-sm text-gray-700">
			  <input
				type="checkbox"
				checked={!!showThirdParty}
				onChange={(e) => setShowThirdParty(!!e.target.checked)}
			  />
			  Show 3rd Party (GasSearch)
			</label>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!showMonthlyProd}
                onChange={(e) => {
                  const on = !!e.target.checked;
                  setShowMonthlyProd(on);
                  if (!on) setMonthlyProdMode("both");
                }}
              />
              Show Monthly Production
            </label>

            {showMonthlyProd ? (
              <>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="text-xs text-gray-500">Type</span>
                  <select
                    className="rounded-xl border border-gray-200 px-3 py-2 bg-white"
                    value={monthlyProdMode}
                    onChange={(e) => setMonthlyProdMode(e.target.value)}
                  >
                    <option value="both">Oil + Gas</option>
                    <option value="oil">Oil only</option>
                    <option value="gas">Gas only</option>
                  </select>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="text-xs text-gray-500">Prod Year</span>
                  <input
                    className="w-[110px] rounded-xl border border-gray-200 px-3 py-2"
                    value={prodYear}
                    onChange={(e) => setProdYear(e.target.value)}
                  />
                </div>
              </>
            ) : null}

            <div className="text-xs text-gray-500">
              Use <span className="font-semibold">Columns</span> to pick fields + scenarios + negotiable deltas.
            </div>
          </div>
        </div>
      </Section>

      {/* Results */}
      <div className="rounded-2xl border border-gray-200 bg-white p-2 md:p-3">
        <div className="flex items-center justify-between mb-2 px-2">
          <div className="text-sm font-semibold text-gray-800">
            Results {filteredSortedRows.length ? `(${filteredSortedRows.length})` : ""}
          </div>
          <div className="text-xs text-gray-500">Click header to sort · “Filter” adds a column filter · click row to open</div>
        </div>

        <div
		  className="relative overflow-auto rounded-xl border border-gray-100"
		  style={{ maxHeight: "70vh" }}
		>
          <table className="min-w-[1400px] w-full text-sm border-separate border-spacing-0">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                {visibleColumns.map((c) => {
                  const isSorted = sort?.key === c.key;
                  const indicator = isSorted ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

                  return (
                    <th
					  key={c.key}
                      ref={(el) => (headerCellRefs.current[c.key] = el)}
                      style={pinnedStyle(c.key, true) || undefined}
                      onContextMenu={(e) => openCtxMenu(e, c)}
					  className="py-2 px-3 whitespace-nowrap bg-gray-50"
					  draggable
					  onDragStart={(e) => {
						setDragColKey(c.key);
						e.dataTransfer.effectAllowed = "move";
						try {
						  e.dataTransfer.setData("text/plain", c.key);
						} catch {}
					  }}
					  onDragOver={(e) => {
						e.preventDefault();
						e.dataTransfer.dropEffect = "move";
					  }}
					  onDrop={(e) => {
						e.preventDefault();
						const from = dragColKey || (() => {
						  try { return e.dataTransfer.getData("text/plain"); } catch { return null; }
						})();
						const to = c.key;
						if (!from || !to || from === to) return;

						// initialize order if empty: current visible order
						const currentVisibleKeys = visibleColumns.map((x) => x.key);
						const currentOrderRaw =
						  (Array.isArray(colUI?.columnOrder) && colUI.columnOrder.length)
							? colUI.columnOrder
							: currentVisibleKeys;

						const currentOrder = normalizeColumnOrder(currentOrderRaw, currentVisibleKeys);

						const nextOrder = moveKeyInOrder(currentOrder, from, to);
						setColumnOrder(nextOrder);
					  }}
					>
                      <div className="flex items-center gap-1.5">
                        <button
                          className="text-left font-semibold"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSort(c.key);
                          }}
                          type="button"
                          title="Sort"
                        >
                          {c.label}
                          {indicator}
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {filteredSortedRows.length === 0 && (
                <tr>
                  <td className="py-4 px-3 text-gray-500" colSpan={visibleColumns.length}>
                    {busy ? "Loading…" : !hasRun ? "Click Run to load results." : "No rows match your filters."}
                  </td>
                </tr>
              )}

              {filteredSortedRows.map((r) => {
                const u = r.unit;

                return (
                  <tr
                    key={u?.id ?? Math.random()}
                    className={[
					  "border-t hover:bg-gray-50",
					  prodPopupOpen && Number(u?.id) === Number(prodPopupUnitId) ? "bg-blue-50/60" : "",
					].join(" ")}
										onClick={() => {
										  // Clicking a row selects it for the production detail popup (if open).
										  // BAID detail still opens ONLY via the BAID link/button.
										  if (!prodPopupOpen) return;
										  const id = u?.id;
										  if (!id) return;
										  setProdPopupUnitId(id);
										  setProdPopupResolved(r?.resolved || {});
										}}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
					  if (editing) return;
					  if (e.key === "Enter" || e.key === " ") openUnit?.(u, taxYear);
					}}
                  >
                    {visibleColumns.map((c) => {
                      const v = getCell(r, c.key);
					  
					  // BAID column: clickable link to open detail
					  if (c.key === "unit.id") {
					    return (
						  <td
						    key={c.key}
						    className="py-2 px-3"
						    style={pinnedStyle(c.key, false) || undefined}
						    onClick={(e) => e.stopPropagation()}
						  >
						    <button
							  type="button"
							  className="font-mono text-blue-600 hover:underline"
							  onClick={(e) => {
							    e.preventDefault();
							    e.stopPropagation();
							    openUnit?.(u, taxYear);
							  }}
							  title="Open BAID detail"
						    >
							  {fmt(v)}
						    </button>
						  </td>
					    );
					  }

                      if (c.type === "bool")
					    return (
						  <td key={c.key} className="py-2 px-3" style={pinnedStyle(c.key, false) || undefined}>
						    {Boolean(v) ? "✅" : ""}
						  </td>
					    );
                      if (c.type === "percent")
					    return (
						  <td key={c.key} className="py-2 px-3" style={pinnedStyle(c.key, false) || undefined}>
						    {fmtPct(v)}
						  </td>
					    );

                      // Inline-editable BA metric cells: m.<code>.ba
                      const editable = isEditableCell(c.key);
                      const isThisEditing =
                        editable &&
                        editing &&
                        editing.unitId === u?.id &&
                        editing.key === c.key;
					  const isEditingBasis =
                        isThisEditing &&
                        editing &&
                        (editing.metric === "loe_basis" ||
                          editing.metric === "tp_start_rate_basis_oil" ||
                          editing.metric === "tp_start_rate_basis_gas");

                      if (isThisEditing) {
                        return (
                          <td
						    key={c.key}
						    className="py-2 px-3"
						    style={pinnedStyle(c.key, false) || undefined}
						    onClick={(e) => e.stopPropagation()}
						  >
                            {isEditingBasis ? (
                              <select
                                autoFocus
                                className="w-full rounded-xl border border-blue-200 px-2 py-1 bg-white"
                                value={String(editValue || "").trim()}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setEditValue(v);
                                  // ✅ Save using the explicit selected value (avoids stale state / double-save issues)
                                  commitEdit(r, v);
                                }}
                                onKeyDown={(e) => {
                                  // keyboard open disabled: detail opens ONLY via BAID click
                                }}
                                onBlur={() => commitEdit(r)}
                              >
                                {editing?.metric === "loe_basis" ? (
                                  <>
                                    <option value="">—</option>
                                    <option value="gross">Gross</option>
                                    <option value="net_of_marketing">Net of Marketing</option>
                                  </>
                                ) : (
                                  <>
                                    <option value="sep_dec">Sep–Dec Avg</option>
                                    <option value="last_1">Last Month</option>
                                    <option value="last_3">Last 3 Mo Avg</option>
                                    <option value="last_4">Last 4 Mo Avg</option>
                                    <option value="last_6">Last 6 Mo Avg</option>
                                    <option value="last_12">Last 12 Mo Avg</option>
                                  </>
                                )}
                              </select>
                            ) : (
                              <input
                                autoFocus
                                className="w-full rounded-xl border border-blue-200 px-2 py-1"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelEdit();
                                  }
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    commitEdit(r);
                                  }
                                }}
                                onBlur={() => commitEdit(r)}
                              />
                            )}
                          </td>
                        );
                      }

                      const isSaving =
                        savingCell && savingCell.unitId === u?.id && savingCell.key === c.key;

                      return (
                        <td
                          key={c.key}
						  style={{
						    ...(pinnedStyle(c.key, false) || {}),
						    ...(heatmapStyleForCell(c.key, v) || {}),
						  }}
                          className={[
                            "py-2 px-3",
                            editable ? "cursor-text hover:bg-blue-50/40" : "",
                          ].join(" ")}
                          onClick={(e) => {
						    if (!editable) return;
						    e.preventDefault();
						    e.stopPropagation();
						    startEdit(r, c.key);
						  }}
						  title={editable ? "Click to edit" : undefined}
                          >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">{fmt(v)}</div>
                            {isSaving ? <div className="text-xs text-gray-400">Saving…</div> : null}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
		{ctxMenu?.col ? (
          <div
            className="fixed z-50 rounded-xl border border-gray-200 bg-white shadow-lg text-sm"
            style={{ left: ctxMenu.x, top: ctxMenu.y, minWidth: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                setSort({ key: ctxMenu.col.key, dir: "asc" });
                setCtxMenu(null);
              }}
            >
              Sort Asc
            </button>

            <button
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                setSort({ key: ctxMenu.col.key, dir: "desc" });
                setCtxMenu(null);
              }}
            >
              Sort Desc
            </button>

            <button
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                openFilterPrompt(ctxMenu.col);
                setCtxMenu(null);
              }}
            >
              Filter…
            </button>

            <div className="my-1 border-t border-gray-100" />

            <button
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                removeColumnFromSelection(ctxMenu.col.key);
                setCtxMenu(null);
              }}
            >
              Hide Column
            </button>

            <button
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                togglePinned(ctxMenu.col.key);
                setCtxMenu(null);
              }}
            >
              {isPinned(ctxMenu.col.key) ? "Unpin Column" : "Pin Column"}
            </button>

            <button
              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-600"
              onClick={() => {
                clearPinned();
                setCtxMenu(null);
              }}
            >
              Clear All Pinned
            </button>
          </div>
        ) : null}  
        </div>

        <div className="mt-2 px-2 text-xs text-gray-500">
		  Views are saved to your account (server). Local cache is used as fallback.
		</div>
      </div>
    </div>
  );
}
